import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { useSubscription } from '../../hooks/useSubscription';

interface EmailSubscriptionFormProps {
  className?: string;
  compact?: boolean;
}

export function EmailSubscriptionForm({ className = '', compact = false }: EmailSubscriptionFormProps) {
  const [email, setEmail] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [popupType, setPopupType] = useState<'success' | 'error'>('success');
  const { subscribe, loading, error, success } = useSubscription();

  useEffect(() => {
    if (success) {
      setPopupMessage('Successfully subscribed! You will receive email alerts when new jobs are posted.');
      setPopupType('success');
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 5000);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      if (error.includes('already subscribed')) {
        setPopupMessage('This email is already subscribed to job alerts.');
      } else {
        setPopupMessage(error);
      }
      setPopupType('error');
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 5000);
    }
  }, [error]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) return;

    const result = await subscribe(email.trim());
    if (result) {
      setEmail('');
    }
  };

  if (compact) {
    return (
      <>
        {showPopup && (
          <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
            <div className={`flex items-center gap-3 px-6 py-4 rounded-lg shadow-lg border ${
              popupType === 'success'
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
            }`}>
              {popupType === 'success' ? (
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
              )}
              <p className={`text-sm font-medium ${
                popupType === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>
                {popupMessage}
              </p>
              <button
                onClick={() => setShowPopup(false)}
                className="ml-2 text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit} className={`flex gap-2 ${className}`}>
          <div className="flex-1 relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              required
              disabled={loading}
            />
          </div>
          <button
            type="submit"
            disabled={loading || !email.trim()}
            className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
          >
            {loading ? 'Subscribing...' : 'Subscribe'}
          </button>
        </form>
      </>
    );
  }

  return (
    <>
      {showPopup && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
          <div className={`flex items-center gap-3 px-6 py-4 rounded-lg shadow-lg border ${
            popupType === 'success'
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          }`}>
            {popupType === 'success' ? (
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
            )}
            <p className={`text-sm font-medium ${
              popupType === 'success' ? 'text-green-800' : 'text-red-800'
            }`}>
              {popupMessage}
            </p>
            <button
              onClick={() => setShowPopup(false)}
              className="ml-2 text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
        </div>
      )}
      <div className={`bg-white rounded-xl shadow-lg p-8 ${className}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-amber-100 rounded-lg">
            <Mail className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Get Job Alerts</h3>
            <p className="text-sm text-gray-600">Be the first to know about new opportunities</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              required
              disabled={loading}
            />
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {success && (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-800">
              Successfully subscribed! You'll receive email alerts when new jobs are posted.
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !email.trim()}
          className="w-full py-3 bg-amber-600 text-white font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Subscribing...' : 'Subscribe to Job Alerts'}
        </button>

        <p className="text-xs text-gray-500 text-center">
          You can unsubscribe at any time using the link in our emails.
        </p>
      </form>
      </div>
    </>
  );
}
